/**
 * Sobel edge map on a 480×270 downscale of the plate (§3.2 `geo/edgeMap.worker.ts`).
 *
 * Two customers:
 *  - **snap to image edges** — `makeEdgeProbe()` turns the magnitude buffer
 *    into the `SnapSources.imageEdge` callback the canvas passes to
 *    `snapPoint()`;
 *  - **horizon guess** — `guessHorizonY()` finds the row with the strongest
 *    horizontal gradient, which is what the `H` tool offers as a starting
 *    point on a plate that has no `depth.horizon_y` yet.
 *
 * The main thread does the downscale (a `<canvas>` `drawImage` is hardware
 * accelerated and an `OffscreenCanvas` is not available everywhere), so the
 * worker only ever sees an RGBA buffer. Every function below is pure and
 * exported, which is what the unit tests exercise; the `onmessage` wiring at
 * the bottom only runs inside a real worker.
 */

/** Downscale the plate to this before running Sobel. */
export const EDGE_W = 480
export const EDGE_H = 270

export interface EdgeMap {
  w: number
  h: number
  /** Gradient magnitude per cell, normalised to `0..1`. */
  mag: Float32Array
  /** Vertical component (|gy|), normalised — used for the horizon guess. */
  gy: Float32Array
}

export interface BuildRequest {
  type: 'build'
  id: number
  w: number
  h: number
  /** RGBA bytes, `w * h * 4` long. */
  rgba: ArrayBuffer
}

export interface BuildResponse {
  type: 'edges'
  id: number
  w: number
  h: number
  mag: ArrayBuffer
  gy: ArrayBuffer
  /** Percent-y of the strongest horizontal edge, or `null` when nothing stands out. */
  horizon_y: number | null
}

export type EdgeWorkerRequest = BuildRequest
export type EdgeWorkerResponse = BuildResponse

/** Luma (Rec. 601) of an RGBA buffer, one float per pixel. */
export function toGray(rgba: Uint8ClampedArray | Uint8Array, w: number, h: number): Float32Array {
  const out = new Float32Array(w * h)
  for (let i = 0, p = 0; i < out.length; i++, p += 4) {
    out[i] = (0.299 * (rgba[p] ?? 0) + 0.587 * (rgba[p + 1] ?? 0) + 0.114 * (rgba[p + 2] ?? 0)) / 255
  }
  return out
}

/**
 * 3×3 Sobel over a grayscale buffer. Returns the magnitude and |gy|, both
 * normalised by the largest magnitude found so a flat plate does not produce
 * a map of amplified noise.
 */
export function sobel(gray: Float32Array, w: number, h: number): EdgeMap {
  const mag = new Float32Array(w * h)
  const gyAbs = new Float32Array(w * h)
  if (w < 3 || h < 3) return { w, h, mag, gy: gyAbs }
  const at = (x: number, y: number): number => gray[y * w + x] ?? 0
  let max = 0
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const tl = at(x - 1, y - 1)
      const tc = at(x, y - 1)
      const tr = at(x + 1, y - 1)
      const ml = at(x - 1, y)
      const mr = at(x + 1, y)
      const bl = at(x - 1, y + 1)
      const bc = at(x, y + 1)
      const br = at(x + 1, y + 1)
      const gx = tr + 2 * mr + br - (tl + 2 * ml + bl)
      const gy = bl + 2 * bc + br - (tl + 2 * tc + tr)
      const m = Math.hypot(gx, gy)
      mag[y * w + x] = m
      gyAbs[y * w + x] = Math.abs(gy)
      if (m > max) max = m
    }
  }
  if (max > 0) {
    for (let i = 0; i < mag.length; i++) {
      mag[i] = (mag[i] ?? 0) / max
      gyAbs[i] = (gyAbs[i] ?? 0) / max
    }
  }
  return { w, h, mag, gy: gyAbs }
}

export function buildEdgeMap(rgba: Uint8ClampedArray | Uint8Array, w: number, h: number): EdgeMap {
  return sobel(toGray(rgba, w, h), w, h)
}

/** Strength threshold a cell must clear to count as "a real edge". */
export const EDGE_THRESHOLD = 0.35

/**
 * Percent-y of the row whose horizontal-gradient energy is highest — the
 * classic "sky meets ground" line. `null` when no row stands out (a plate
 * with no horizon, or a flat fill).
 */
export function guessHorizonY(map: EdgeMap, threshold = EDGE_THRESHOLD): number | null {
  const { w, h, gy } = map
  if (w < 3 || h < 3) return null
  let bestRow = -1
  let bestScore = 0
  let total = 0
  for (let y = 1; y < h - 1; y++) {
    let score = 0
    const base = y * w
    for (let x = 1; x < w - 1; x++) {
      const v = gy[base + x] ?? 0
      if (v >= threshold) score += v
    }
    total += score
    if (score > bestScore) {
      bestScore = score
      bestRow = y
    }
  }
  // A horizon is a *line*: its row must carry clearly more energy than the
  // average row, otherwise we are looking at texture and should say nothing.
  const avg = total / Math.max(1, h - 2)
  if (bestRow < 0 || bestScore < Math.max(0.2 * w * threshold, avg * 3)) return null
  return ((bestRow + 0.5) / h) * 100
}

/**
 * Build the `SnapSources.imageEdge` probe: given a percent point and a
 * percent tolerance, return the strongest edge cell within that radius (as a
 * percent point) or `null`.
 */
export function makeEdgeProbe(map: EdgeMap, threshold = EDGE_THRESHOLD): (p: [number, number], tolPct: number) => [number, number] | null {
  const { w, h, mag } = map
  return (p, tolPct) => {
    if (w < 3 || h < 3 || tolPct <= 0) return null
    const cx = (p[0] / 100) * w
    const cy = (p[1] / 100) * h
    const rx = Math.max(1, Math.ceil((tolPct / 100) * w))
    const ry = Math.max(1, Math.ceil((tolPct / 100) * h))
    const x0 = Math.max(1, Math.floor(cx) - rx)
    const x1 = Math.min(w - 2, Math.floor(cx) + rx)
    const y0 = Math.max(1, Math.floor(cy) - ry)
    const y1 = Math.min(h - 2, Math.floor(cy) + ry)
    let best: [number, number] | null = null
    let bestScore = -Infinity
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const m = mag[y * w + x] ?? 0
        if (m < threshold) continue
        const dx = (x + 0.5 - cx) / Math.max(1, rx)
        const dy = (y + 0.5 - cy) / Math.max(1, ry)
        const d2 = dx * dx + dy * dy
        if (d2 > 1) continue
        // Prefer strong edges, break ties by proximity.
        const score = m - 0.25 * Math.sqrt(d2)
        if (score > bestScore) {
          bestScore = score
          best = [((x + 0.5) / w) * 100, ((y + 0.5) / h) * 100]
        }
      }
    }
    return best
  }
}

/* -------------------------------------------------------------------------- */
/* Worker wiring — only inside a real DedicatedWorkerGlobalScope.              */
/* -------------------------------------------------------------------------- */

/**
 * The DOM and WebWorker lib types cannot both be loaded, so the worker scope
 * is reached through `globalThis` with a hand-written shape rather than
 * `DedicatedWorkerGlobalScope`. `WorkerGlobalScope` exists only inside a real
 * worker, which is exactly the guard we want: importing this module from a
 * test or from the main thread registers nothing.
 */
interface WorkerScope {
  WorkerGlobalScope?: unknown
  addEventListener(type: 'message', listener: (ev: MessageEvent<EdgeWorkerRequest>) => void): void
  postMessage(message: BuildResponse, transfer: Transferable[]): void
}

export function handleBuild(req: BuildRequest): BuildResponse {
  const map = buildEdgeMap(new Uint8ClampedArray(req.rgba), req.w, req.h)
  return {
    type: 'edges',
    id: req.id,
    w: map.w,
    h: map.h,
    mag: map.mag.buffer as ArrayBuffer,
    gy: map.gy.buffer as ArrayBuffer,
    horizon_y: guessHorizonY(map),
  }
}

const scope = globalThis as unknown as WorkerScope

if (typeof scope.WorkerGlobalScope !== 'undefined' && typeof scope.postMessage === 'function') {
  scope.addEventListener('message', (ev) => {
    const req = ev.data
    if (!req || req.type !== 'build') return
    const res = handleBuild(req)
    scope.postMessage(res, [res.mag, res.gy])
  })
}
